import { first } from '../core/dom.js';

/**
 * Quiz events (spec §1).
 *
 * DNK Quiz (Plugins/dnk-quiz — "DNK" is Slovenian for DNA) owns the quiz UI and knows
 * things this collector can only guess at from the DOM — which gender was clicked, which
 * screen is showing and what kind it is, the result segment. It has no tracking vocabulary
 * of its own: it just announces plain facts on the `dnk-quiz:*` DOM CustomEvent channel
 * (`{ detail }`, no event name, no dedupe key), and this collector is the only place that
 * decides what, if anything, each one becomes — including the question_type → step_kind
 * mapping below, which is a tracking taxonomy decision, not a fact dnk-quiz should have to
 * know. Every quiz event is fact-driven now — none of them are derived from the DOM any
 * more. quiz_step_viewed comes from every screen dnk-quiz renders (`dnk-quiz:step-viewed`);
 * quiz_step_answered from every field of a multi-field group step
 * (`dnk-quiz:field-answered`) and every other answer (`dnk-quiz:step-answered`), both on
 * dnk-quiz's own `getVisibleIndices()` position for `step_number`/`total_steps` (the old DOM
 * fallback for quiz_step_answered read the *visible progress bar's* narrower "counted steps
 * only" total while quiz_step_viewed already counted every screen, so the two disagreed on
 * `total_steps` for the same visit); quiz_completed from `dnk-quiz:quiz-completed`, fired
 * once the quiz variant's actual completion signal succeeds — the lead-capture form's save,
 * or (for a quiz with no such form) the answer-save AJAX on its own — never the earlier
 * click on the last question, which is not completion.
 *
 * Every event but the gender screen's own `quiz_step_viewed` (step 0) also carries
 * `quiz_variant` — `"male_13"` / `"female_14"`, gender plus the total step count for that
 * branch. Gender decides which questions apply, so the two genders don't just answer
 * differently — they take quizzes of different length, and the same question_id lands at a
 * different step_number in each. dnk-quiz reports gender+totalSteps twice: on
 * `dnk-quiz:gender-selected` (the click) and on `dnk-quiz:gender-restored` (page load with
 * a gender already saved from an earlier visit — a reload mid-quiz, say, which never fires
 * the click). Both just update this collector's own `quizVariant` and `total_steps`,
 * computed once and reused for every later event, since the branch can't change once
 * gender is fixed; gender-restored never itself calls tracker.track — nothing was
 * "started" this session.
 *
 * `total_steps` itself is captured the same way, for the same reason `quiz_variant` is —
 * dnk-quiz used to recompute it fresh at every single event (each call site running its
 * own `getVisibleIndices()`, and `quiz-completed` reading a different variable,
 * `questions_length`, entirely), so a visitor could see 0, 6, 10, 12, 13 and 14 all in one
 * session as answers changed which later questions applied. One session, one total.
 *
 * quiz_results_screen_viewed is the one exception to "every event comes from a DOM
 * CustomEvent on this page" — the actual results page, for this funnel, is the
 * recommended-product page dnk-quiz redirects to once the quiz finishes, which has no
 * `.dnkqz-quiz-widget` (or any dnk-quiz script) on it at all, so nothing there can fire
 * a CustomEvent this collector could listen for. `dnk-quiz:quiz-completed` — already
 * fired right before that redirect, on this same page, while `quizVariant`/
 * `quizTotalSteps` are still live — carries `resultUrl`: the actual URL dnk-quiz is
 * about to navigate to for results, or `null` when this completion instead goes to its
 * `?dnk_p=inf` thank-you-then-home branch, resolved by dnk-quiz itself the same way it
 * resolves which branch to take, not guessed at from this page's URL. When `resultUrl`
 * is present, the `dnk-quiz:quiz-completed` listener below leaves itself a one-shot note
 * in `sessionStorage` (this page's own `quizVariant`/`quizTotalSteps`, not the URL
 * itself — nothing on the *next* page needs it). `reportPendingResultsScreen` below
 * reads that note once, ahead of this file's usual widget check, since the results page
 * is never the quiz page.
 */

export const QUIZ_WIDGET = '.dnkqz-quiz-widget';

/**
 * dnk-quiz reports a screen's raw `question_type` (its own JSON field), not a step_kind —
 * classifying what counts as "interstitial" vs. "results" is a reporting decision, made
 * here. `bmi_result` and `goal_result` compute and show a number from the visitor's own
 * answers (their BMI, their projected loss); everything else content-only is the same copy
 * for every visitor in that gender branch — social proof, formula pitches, expectation-
 * setting — so it's "interstitial", not a result. Anything not listed is a real question.
 */
const STEP_KIND_BY_QUESTION_TYPE = {
  bmi_result: 'results',
  goal_result: 'results',
  motivation_intro: 'interstitial',
  goals_motivation: 'interstitial',
  results_expectations_female: 'interstitial',
  results_expectations_male: 'interstitial',
  metabolic_blockers: 'interstitial',
  // Not a `questions[]` entry — the loading overlay dnk-quiz shows on its own timer (no
  // Next button, no click of any kind) while calculating BMI. It shares its step_number
  // with the question it overlays, which is exactly why quiz_step_viewed dedupes on
  // question_id below rather than step_number: two different screens, same number.
  motivation_ai_overlay: 'interstitial',
};

function stepKindFor(questionType) {
  return STEP_KIND_BY_QUESTION_TYPE[questionType] || 'question';
}

/**
 * Renames a dnk-quiz question id to whatever a downstream query should actually see.
 * `height`/`weight` alone don't say the unit, and this is the one place a downstream query
 * has to get that unit right (the BMI formula divides height in metres).
 *
 * `bmi_result` renames to `results_screen` for a different reason: its JSON `label` is a
 * per-locale translated heading ("Vaši rezultati:" / "Az eredményei:" / "Twoje wyniki:" /
 * "Your results:"), and question_id must stay one fixed value regardless of which language
 * the visitor sees — a downstream query can't group visitors across locales by a
 * `question_id` that changes with the language. The translation itself isn't lost, just
 * moved to its own field (see `question_title` below). `goal_result` is left alone: its
 * `label` is always empty in every quiz configuration, so it's never actually exhibited
 * this problem, and renaming it too would make its dedupe_key collide with bmi_result's —
 * two genuinely different screens, sharing one dedupe key.
 *
 * Anything not listed (currently just `birth_year`) is reported under its own id unchanged.
 */
const QUESTION_ID_OVERRIDES = {
  height: 'height_cm',
  weight: 'weight_kg',
  bmi_result: 'results_screen',
};

function overrideQuestionId(questionId) {
  return QUESTION_ID_OVERRIDES[questionId] || questionId;
}

function quizVariantFrom(gender, totalSteps) {
  const genderPrefix = gender === 'Moški' ? 'male' : gender === 'Ženska' ? 'female' : undefined;

  return genderPrefix && totalSteps ? genderPrefix + '_' + totalSteps : undefined;
}

// Shared between collectQuiz's dnk-quiz:quiz-completed listener (writes) and
// reportPendingResultsScreen at the bottom of this file (reads) — kept up here with the
// other module-level constants rather than only next to the one that reads it.
const PENDING_RESULTS_KEY = 'dnkqz_pending_results_screen';

export function collectQuiz(tracker, { win = window, doc = document } = {}) {
  // Runs before the .dnkqz-quiz-widget check below on purpose — see
  // reportPendingResultsScreen's own comment, at the bottom of this file, for why.
  reportPendingResultsScreen(tracker, win);

  const widget = first(QUIZ_WIDGET, doc);

  if (!widget) {
    return null;
  }

  // Checked again before every emission, not just here. dnk-quiz sets the flag inside a
  // jQuery(fn) block, so it lands at DOM-ready — after this bundle has parsed and run.
  // Reading it once at boot therefore always saw `undefined` and both implementations
  // reported the whole funnel.
  const bridgeIsReporting = () => Boolean(win.dnkqzTrackdaddy);

  if (bridgeIsReporting()) {
    // The quiz reports for itself, with better data than the DOM can give us.
    return null;
  }

  // String, always. dnk-quiz reads the same attributes with jQuery .data(), which coerces
  // an all-digit value to a Number — and `properties` is a ClickHouse JSON column whose
  // path type is inferred once, so the second emitter's rows would be dropped.
  const quizId = String(widget.getAttribute('data-quiz-id') || 'dnk');

  // How many times quiz_step_answered has reported this exact question this session —
  // keyed the same way as the event's own dedupe_key. A visitor who changes their mind and
  // answers the same question again is not a bug to suppress (going back and revising an
  // answer is normal), but the two rows need telling apart: the highest answer_revision for
  // a given dedupe_key is the one that should win downstream.
  const answerRevisions = new Map();

  function nextAnswerRevision(key) {
    const revision = (answerRevisions.get(key) || 0) + 1;
    answerRevisions.set(key, revision);

    return revision;
  }

  // Gender fixes which branch a visitor is on, and a branch's total step count never
  // changes once picked — so both are computed once, here, and every later event just
  // reuses the same values instead of trusting its own (possibly stale, possibly 0)
  // totalSteps.
  let quizVariant;
  let quizTotalSteps;

  function applyQuizBranch(gender, totalSteps) {
    // Never a literal 0 — that's not a real branch length, just a visible_if computed too
    // early, so treat it the same as "not known yet".
    quizTotalSteps = totalSteps || undefined;
    quizVariant = quizVariantFrom(gender, quizTotalSteps);
  }

  // DNK Quiz dispatches this the instant "Sem moški" / "Sem ženska" is clicked — on a
  // screen outside `.dnkqz-quiz-widget`, before the quiz itself is even visible. Without a
  // dedicated signal for that click, quiz_started could only fire together with the first
  // in-widget answer, which collapsed "never picked a gender" and "picked one, answered
  // nothing" into the same zero.
  doc.addEventListener('dnk-quiz:gender-selected', (event) => {
    const detail = (event && event.detail) || {};

    applyQuizBranch(detail.gender, detail.totalSteps);

    tracker.track(
      'quiz_started',
      {
        quiz_id: quizId,
        quiz_type: detail.quizType || 'general',
        quiz_variant: quizVariant,
        total_steps: quizTotalSteps,
        answer_value: detail.gender,
      },
      { once: 'session', dedupeKey: 'quiz_started:' + quizId }
    );
  });

  // DNK Quiz dispatches this on page load when a gender was already picked on an earlier
  // visit (its own localStorage restore) — a reload mid-quiz, say. There is no click to
  // hang quiz_started off here, and this visitor didn't just start anything, so this only
  // recovers quiz_variant for whatever events follow; it never itself calls tracker.track.
  doc.addEventListener('dnk-quiz:gender-restored', (event) => {
    const detail = (event && event.detail) || {};

    applyQuizBranch(detail.gender, detail.totalSteps);
  });

  // DNK Quiz dispatches this as soon as the gender screen renders — which, since nothing
  // hides it until a gender is clicked, is on every load. It is step 0, with its own kind
  // and no total_steps: the real total depends on which branch the still-unknown gender
  // sends the visitor down, so any number reported here would just be wrong.
  doc.addEventListener('dnk-quiz:gender-screen-viewed', () => {
    tracker.track(
      'quiz_step_viewed',
      {
        quiz_id: quizId,
        step_number: 0,
        question_id: 'gender_select',
        step_kind: 'gender',
        // Not known yet — the whole point of this step is that gender (and therefore the
        // branch) hasn't been picked.
        quiz_variant: undefined,
      },
      { dedupeKey: 'quiz_step_viewed:' + quizId + ':gender_select' }
    );
  });

  // DNK Quiz dispatches this for every screen it renders after the gender screen — a real
  // question exactly as much as a testimonial or a results screen, since a visitor spends
  // time on all of them and a step number that skips the ones with no input undercounts
  // the funnel. `stepNumber` is dnk-quiz's own position of this screen among every screen
  // this visitor's branch will show (not a render counter — going back reports the same
  // number again, it doesn't drift forward), so it accounts for gender-branching in a way
  // no DOM text ever could.
  //
  // Deliberately no `once: 'session'` here: the spec calls this "each question render,
  // including going back", and a visitor who goes back to change an earlier answer and
  // re-reaches a results screen (bmi_result, say) with a genuinely different computed
  // value needs that reported again, not silently dropped because the same question_id
  // fired once already. `dedupeKey` still rides the envelope's `dedupe_key` for whatever
  // downstream collapsing needs it; question_id (not step_number) is what it's built from,
  // because the motivation_ai loading overlay intentionally shares its step_number with the
  // question it overlays (see STEP_KIND_BY_QUESTION_TYPE above) — two different screens can
  // share a number, but never a question_id.
  doc.addEventListener('dnk-quiz:step-viewed', (event) => {
    const detail = (event && event.detail) || {};

    if (!detail.stepNumber || !detail.questionId) {
      return;
    }

    const questionId = overrideQuestionId(detail.questionId);

    tracker.track(
      'quiz_step_viewed',
      {
        quiz_id: quizId,
        step_number: detail.stepNumber,
        total_steps: quizTotalSteps,
        question_id: questionId,
        // dnk-quiz's raw, per-locale JSON `label` for this screen — a plain fact, never a
        // tracking decision, which is exactly why question_id itself must never be this:
        // "Vaši rezultati:" / "Az eredményei:" / "Twoje wyniki:" are the same screen in
        // three languages, and a downstream query needs one stable key across all of them.
        question_title: detail.questionTitle || undefined,
        step_kind: stepKindFor(detail.questionType),
        quiz_variant: quizVariant,
      },
      { dedupeKey: 'quiz_step_viewed:' + quizId + ':' + questionId }
    );
  });

  // Both listeners below share this: dedupe_key used to be
  // `quiz_step_answered:{session_id}:{event_id}` — event_id is a fresh UUID on every call,
  // so the key was never the same value twice and could never catch a real duplicate.
  // `step_number` alone isn't enough either — a group step's fields (birth_year, height_cm,
  // weight_kg) all share one, so a step_number-keyed dedupe would collapse three distinct
  // answers into one, exactly what dnk-quiz:field-answered exists to stop. question_id is
  // the one component that's narrow enough to isolate a true repeat and wide enough to
  // leave every field of a group answer alone — the same reasoning quiz_step_viewed's
  // dedupe key already follows, for the same underlying shared-step_number problem.
  function answeredDedupeKey(quizType, questionId) {
    return 'quiz_step_answered:' + quizId + ':' + quizType + ':' + questionId;
  }

  // Second line of defence, not the only one — dnk-quiz is expected to never dispatch a
  // fact event for these fields at all (a "group" step can just as well collect name +
  // email as a lead-capture step as it can birth year/height/weight, and a plain "text"
  // question can be a name field the same way). If it ever does anyway — a future call
  // site that forgets the guard, a differently-configured quiz — this collector still
  // refuses to let a raw name or e-mail become `answer_value`. quiz_lead_submitted, with
  // a hashed email, is the only event allowed to say anything about either.
  const SENSITIVE_QUESTION_IDS = new Set(['email', 'lead_email', 'name', 'lead_name']);

  // DNK Quiz dispatches this once per field of a multi-field group step (currently just
  // the BMI calculation: birth year, height, weight), re-reporting every field — not only
  // the ones the visitor actually touched — once validation passes, so a field left at its
  // default (never fires its own `change`, but is still submitted) is still reported.
  doc.addEventListener('dnk-quiz:field-answered', (event) => {
    const detail = (event && event.detail) || {};

    if (!detail.stepNumber || !detail.fieldId || SENSITIVE_QUESTION_IDS.has(detail.fieldId)) {
      return;
    }

    const quizType = detail.quizType || 'general';
    const questionId = overrideQuestionId(detail.fieldId);
    const dedupeKey = answeredDedupeKey(quizType, questionId);

    tracker.track(
      'quiz_step_answered',
      {
        quiz_id: quizId,
        quiz_type: quizType,
        quiz_variant: quizVariant,
        step_number: detail.stepNumber,
        total_steps: quizTotalSteps,
        question_id: questionId,
        answer_value: String(detail.value || '').slice(0, 120),
        answer_revision: nextAnswerRevision(dedupeKey),
      },
      { dedupeKey }
    );
  });

  // DNK Quiz dispatches this for every other answered step — one value, one question —
  // once validation passes, on the same `getVisibleIndices()` position as its own
  // dnk-quiz:step-viewed.
  doc.addEventListener('dnk-quiz:step-answered', (event) => {
    const detail = (event && event.detail) || {};

    if (!detail.stepNumber || !detail.questionId || SENSITIVE_QUESTION_IDS.has(detail.questionId)) {
      return;
    }

    const quizType = detail.quizType || 'general';
    const questionId = overrideQuestionId(detail.questionId);
    const dedupeKey = answeredDedupeKey(quizType, questionId);

    tracker.track(
      'quiz_step_answered',
      {
        quiz_id: quizId,
        quiz_type: quizType,
        quiz_variant: quizVariant,
        step_number: detail.stepNumber,
        total_steps: quizTotalSteps,
        question_id: questionId,
        answer_value: String(detail.value || '').slice(0, 120),
        answer_revision: nextAnswerRevision(dedupeKey),
      },
      { dedupeKey }
    );
  });

  // DNK Quiz dispatches this once the lead-capture form's save has actually succeeded —
  // never on the click alone, which can fail validation. `email_hash` arrives already
  // hashed (dnk-quiz never hands this collector a raw e-mail address to begin with);
  // `consent` has nothing behind it yet — no quiz defines a consent question or control
  // today — so it is always `false` until a real one exists to read.
  doc.addEventListener('dnk-quiz:lead-submitted', (event) => {
    const detail = (event && event.detail) || {};

    tracker.track(
      'quiz_lead_submitted',
      {
        quiz_id: quizId,
        quiz_type: detail.quizType || 'general',
        quiz_variant: quizVariant,
        total_steps: quizTotalSteps,
        email_hash: detail.emailHash || undefined,
        name_filled: Boolean(detail.nameFilled),
        consent: false,
      },
      { once: 'session', dedupeKey: 'quiz_lead_submitted:' + quizId }
    );
  });

  // DNK Quiz dispatches this once the quiz is genuinely done — for a quiz with a
  // dedicated lead-capture screen, that is this same successful save, not the earlier
  // click on the last question (see dnk-quiz:lead-submitted above); for a quiz with no
  // such screen, it's dnkqz_save_answers_with_instant_attribute succeeding on its own.
  // `result_segment` is deliberately absent — the recommended SKU lives in a separate AJAX
  // response dnk-quiz doesn't thread through here.
  doc.addEventListener('dnk-quiz:quiz-completed', (event) => {
    const detail = (event && event.detail) || {};

    tracker.track(
      'quiz_completed',
      {
        quiz_id: quizId,
        quiz_type: detail.quizType || 'general',
        quiz_variant: quizVariant,
        total_steps: quizTotalSteps,
        reporter: 'dnk_quiz',
      },
      { once: 'session', dedupeKey: 'quiz_completed:' + quizId }
    );

    // detail.resultUrl is the actual URL dnk-quiz is about to navigate to for results,
    // or null when this completion instead goes to the unrelated dnk_p=inf
    // thank-you-then-home branch — dnk-quiz resolves this itself, the same way it
    // resolves which branch to actually take. The destination page has no quiz widget
    // on it at all, so this collector leaves itself a one-shot note in sessionStorage —
    // this page's own quizVariant/quizTotalSteps, since there's no such state left once
    // the browser has actually navigated away (the URL itself isn't needed there).
    // reportPendingResultsScreen (below) picks it up there.
    if (detail.resultUrl) {
      try {
        win.sessionStorage.setItem(
          PENDING_RESULTS_KEY,
          JSON.stringify({
            quizId,
            quizType: detail.quizType || 'general',
            quizVariant,
            totalSteps: quizTotalSteps,
          })
        );
      } catch (e) {}
    }
  });

  return true;
}

/**
 * quiz_results_screen_viewed belongs to the actual results page — for this funnel,
 * the recommended-product page dnk-quiz redirects to once the quiz is done — not to
 * any step inside the quiz itself. That page has no `.dnkqz-quiz-widget` on it at all,
 * so nothing here can otherwise tell this visitor just finished a quiz. The
 * `dnk-quiz:quiz-completed` listener above leaves a one-shot note in sessionStorage,
 * already carrying this page's own `quizVariant`/`quizTotalSteps` — there's no such
 * state left to compute once the browser has actually navigated away — and this reads
 * it exactly once, ahead of collectQuiz's usual `.dnkqz-quiz-widget` check, since the
 * results page is never the quiz page itself. Placed last in the file, after
 * `collectQuiz()`, since it's read-side logic that only matters once a quiz-completed
 * write has already happened — everything above runs first; this runs after.
 */
function reportPendingResultsScreen(tracker, win) {
  let raw;

  try {
    raw = win.sessionStorage.getItem(PENDING_RESULTS_KEY);

    if (raw) {
      win.sessionStorage.removeItem(PENDING_RESULTS_KEY);
    }
  } catch (e) {
    return;
  }

  if (!raw) {
    return;
  }

  let pending;

  try {
    pending = JSON.parse(raw);
  } catch (e) {
    return;
  }

  const quizId = String(pending.quizId || 'dnk');

  tracker.track(
    'quiz_results_screen_viewed',
    {
      quiz_id: quizId,
      quiz_type: pending.quizType || 'general',
      quiz_variant: pending.quizVariant,
      total_steps: pending.totalSteps,
    },
    { once: 'session', dedupeKey: 'quiz_results_screen_viewed:' + quizId }
  );
}
